import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { COMPANY } from "@/data/content";

const SiteCtx = createContext(null);

function digits(v) { return (v || "").replace(/[^0-9]/g, ""); }

export function SiteProvider({ children }) {
  const [config, setConfig] = useState(null);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteContext, setQuoteContext] = useState("");

  useEffect(() => {
    api
      .get("/config")
      .then(({ data }) => setConfig(data))
      .catch((err) => {
        // Loud on purpose: silently falling back to the hardcoded defaults
        // below previously hid a real bug (GET /config failing) behind what
        // looked like a normal Hindustan Solar Power page even on the
        // SunExpert deployment — see api.js for the root cause that was
        // actually happening here.
        console.error("GET /config failed — falling back to default brand identity:", err);
        setConfig({});
      });
  }, []);

  const openQuote = (ctx = "") => { setQuoteContext(ctx); setQuoteOpen(true); };

  const waNumber = digits(config?.whatsapp);
  const phoneNumber = digits((config?.phone || "").split(",")[0]);
  const brandName = config?.brand || "Hindustan Solar Power";
  const waLink = (msg) =>
    `https://wa.me/${waNumber || ""}?text=${encodeURIComponent(msg || `Hello ${brandName}, I would like to discuss my solar/energy requirement.`)}`;
  const telLink = phoneNumber ? `tel:+${phoneNumber}` : "tel:";

  return (
    <SiteCtx.Provider value={{ config, setConfig, quoteOpen, setQuoteOpen, openQuote, quoteContext, waLink, telLink, waNumber, phoneNumber }}>
      {children}
    </SiteCtx.Provider>
  );
}

export const useSite = () => useContext(SiteCtx);

// Phase 12: single place every page/component reads brand identity from,
// instead of each file separately mixing `config?.x || COMPANY.x` fallbacks.
// Sourced from the live, site-aware GET /api/config response (see
// backend-node/src/lib/siteContext.js's registry) with the current
// Hindustan Solar Power values as the fallback default — so nothing changes
// visually until /api/config actually reports a different brand.
export function useBrand() {
  const site = useSite();
  const config = site?.config;
  const domain = config?.domain || COMPANY.domain;
  return {
    name: config?.brand || COMPANY.name,
    domain,
    tagline: config?.tagline || COMPANY.tagline,
    positioning: config?.positioning || COMPANY.positioning,
    description: config?.description || COMPANY.description,
    logo: config?.logo || "/logo.jpeg",
    privacyPolicyUrl: config?.privacy_policy_url || `https://${domain}/privacy-policy`,
  };
}
